import React from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { Tooltip } from "antd"
import { GlobalSearchField } from "./GlobalSearchField"
import { useGlobalSearchStore } from "./store"
import type { Shell } from "./types"

/**
 * Desktop: the live search field in the centre of the top bar, with its results
 * dropping down beneath it.
 *
 * Also the single mount point for the field, which is what keeps exactly one
 * search input in the DOM. The mobile icon below opens this same instance; when
 * it does, the field re-presents itself as a sheet through a portal, so it is
 * unaffected by this slot being hidden at small widths.
 */
export const GlobalSearchTrigger: React.FC<{
  shell: Shell
  className?: string
}> = ({ shell, className = "" }) => (
  <GlobalSearchField
    shell={shell}
    className={className}
  />
)

/**
 * Mobile trigger. Sits in the profile cluster and uses the same 32px treatment
 * as the icons beside it, so it reads as part of that set.
 */
export const GlobalSearchIconTrigger: React.FC = () => {
  const openFromIcon = useGlobalSearchStore((state) => state.openFromIcon)

  return (
    <Tooltip title="Search">
      <button
        type="button"
        onClick={openFromIcon}
        aria-label="Search pages and features"
        className="flex h-8 w-8 items-center justify-center text-primary500 transition-colors duration-200 hover:text-primary700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary500"
      >
        <MagnifyingGlassIcon className="h-5 w-5" />
      </button>
    </Tooltip>
  )
}
